import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { Loader2 } from "lucide-react";
import { cn } from "@/lib/utils";

const buttonVariants = cva(
  // Focus halo verified identical across Primary/Filled, Neutral/Stroke and
  // Neutral/Ghost focused nodes in Figma - a universal double ring (2px
  // surface + 4px Gray/100) that REPLACES the default-state shadow.
  "inline-flex items-center justify-center gap-2 whitespace-nowrap font-semibold transition-[filter,background-color,border-color,box-shadow] disabled:pointer-events-none focus-visible:outline-none focus-visible:shadow-[0_0_0_2px_var(--ads-surface),0_0_0_4px_var(--ads-disabled-soft)] [&>svg]:size-4 [&>svg]:shrink-0",
  {
    variants: {
      variant: {
        // Primary/Filled per Figma: Default state is a 1px Primary/600 border,
        // the Primary/500 to 600 gradient, and an inset surface highlight
        // shadow. Hover is a FLAT Button/primary-hover fill with border/shadow
        // removed. Focused drops the gradient for a flat Primary/600 fill
        // with no border. Disabled is a flat bg-primary (surface) fill with
        // disabled text, no border, no shadow, no gradient.
        primary:
          "border border-primary bg-gradient-to-b from-[var(--ads-primary-500)] to-primary text-on-primary shadow-[inset_0_0_2px_2px_rgba(255,255,255,0.3)] hover:border-transparent hover:bg-none hover:bg-[var(--ads-button-primary-hover)] hover:shadow-none focus-visible:border-transparent focus-visible:bg-none focus-visible:bg-primary disabled:border-transparent disabled:bg-none disabled:bg-bg disabled:text-[var(--ads-text-disabled)] disabled:shadow-none",
        success: "bg-success text-white hover:bg-success disabled:opacity-40",
        // Neutral/Stroke per Figma (Default: surface + Border/border-secondary
        // + a subtle shadow - Border/border-secondary is NOT the same as this
        // project's --ads-line-strong, so it's its own token). Hover drops
        // the border and shadow entirely for a flat bg-primary (surface tint)
        // fill. Focused stays on the surface fill with the border darkened to
        // Gray/900. Disabled is a flat bg-primary (surface tint) fill, no
        // border, no shadow, disabled text.
        secondary:
          "border border-[var(--ads-border-secondary)] bg-surface text-ink shadow-[0_1px_2px_rgba(82,88,102,0.06)] hover:border-transparent hover:bg-bg hover:shadow-none focus-visible:border-[var(--ads-gray-900)] focus-visible:bg-surface disabled:border-transparent disabled:bg-bg disabled:text-[var(--ads-text-disabled)] disabled:shadow-none",
        // Neutral/Ghost per Figma (Default: fully transparent, no border,
        // Text/primary text, i.e. this project's text-ink token). Hover tints
        // to bg-primary (surface tint) and never gains a border. Focused
        // converges to the same surface+Gray/900-border look as secondary's
        // focused state. Disabled carries no background change at all - only
        // the text dims to the disabled token.
        ghost:
          "border border-transparent bg-transparent text-ink hover:bg-bg focus-visible:border-[var(--ads-gray-900)] focus-visible:bg-surface disabled:text-[var(--ads-text-disabled)]",
        destructive: "bg-danger text-white hover:bg-danger disabled:opacity-40",
        link: "bg-transparent text-primary underline-offset-4 hover:underline p-0 min-h-0 disabled:opacity-40",
      },
      // Figma sizes: Small/Medium share Radius/s (8px); Large uses Radius/m
      // (10px). Large's own font token is size/Small (14px), same as Medium
      // - not a bigger 16px face.
      size: {
        sm: "min-h-[32px] rounded px-[10px] text-xs",
        md: "min-h-[36px] rounded px-3.5 text-sm",
        lg: "min-h-[40px] rounded-[10px] px-5 text-sm",
      },
      // Figma's "Only Icon=On" variants (e.g. 22:2212/2214/2216) are square
      // at the same height as their text sibling, with no horizontal
      // padding — a mode of this component, not a separate one.
      iconOnly: {
        true: "!px-0",
      },
    },
    compoundVariants: [
      { iconOnly: true, size: "sm", class: "size-8" },
      { iconOnly: true, size: "md", class: "size-9" },
      { iconOnly: true, size: "lg", class: "size-10" },
      // link's own p-0/min-h-0 (Figma has no dedicated Link/text-action
      // component; this is this project's variant) otherwise loses to
      // every size's min-h-[..]/px-* in the class-merge order.
      { variant: "link", class: "!min-h-0 !h-auto !p-0" },
    ],
    defaultVariants: { variant: "primary", size: "md" },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, iconOnly, loading, disabled, children, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size, iconOnly }), loading && "!text-transparent relative", className)}
      disabled={disabled || loading}
      {...props}
    >
      {loading && (
        <Loader2 className="absolute size-3.5 animate-spin text-current opacity-90" data-testid="btn-spinner" />
      )}
      {children}
    </button>
  )
);
Button.displayName = "Button";

export { Button, buttonVariants };
